'use strict';

define('vb/private/helpers/eventHelper',[
  'vb/private/constants',
  'vb/action/eventAction',
  'vb/action/builtin/fireNotificationEventAction',
  'vb/action/builtin/fireCustomEventAction',
  'vb/private/events/eventRegistry',
  'vb/private/events/eventPayloadMetadata',
  'vb/private/log',
  'vb/private/utils',
], (Constants, EventAction, FireNotificationEventAction, FireCustomEventAction, EventRegistry,
  EventPayloadMetadata, Log, Utils) => {
  const logger = Log.getLogger('/vb/private/helpers/eventHelper');
  const {
    canNormalizeEventPayloadRowContextMetadata,
    cloneEventPayloadWithRowContextMetadataHidden,
  } = EventPayloadMetadata;

  /**
   * Clones an at-risk event payload after hiding known row-scoped runtime metadata from enumeration.
   *
   * @param {*} payload event payload
   * @returns {*} original payload or cloned payload when row metadata must be hidden
   */
  const cloneEventPayload = (payload) => (
    payload && canNormalizeEventPayloadRowContextMetadata(payload)
      ? cloneEventPayloadWithRowContextMetadataHidden(payload, (source) => Utils.cloneObject(source))
      : payload
  );

  /**
   * Provides helper methods for working with events in a specific context and is used by built-in actions.
   * The context is encapsulated within the instance, ensuring it remains private.
   */
  class EventHelper {
    /**
     * Constructs a new instance of the EventHelper class.
     *
     * @param {Object} context - The context object passed from the Container.
     * This object must contain a valid container property.
     */
    constructor(context) {
      this._target = null; // default behavior for no target is 'current'

      // context is private via closure

      /**
       * Fires a custom event with the given name and payload.
       *
       * @param {String} name - The name of the event to fire.
       * @param {*} payload - The payload associated with the event.
       * @returns {Promise<*>} A promise that resolves when the event is fired,
       * or rejects if an error occurs.
       */
      this.fireCustomEvent = (name, payload) => Promise.resolve()
        .then(() => {
          if (!context || !context.container) {
            throw new Error('Unable to fire event; no active container context');
          }

          if (!FireCustomEventAction.isValid(name)) {
            throw new Error(`Attempt to raise invalid event: ${name}`);
          }

          const eventPayload = cloneEventPayload(payload);
          const promiseOrNull = EventAction.fireEvent(context.container, name, eventPayload, { target: this._target });
          if (!promiseOrNull) {
            throw new Error(`Unable to find target: ${this._target}`);
          }

          return promiseOrNull.then((resolved) => {
            const eventModel = EventRegistry.get(context.container, context.container, name);
            if (eventModel && !context.container.canFireEvent(eventModel)) {
              logger.warn('Unable to find event declaration for', name,
                '. Please ensure to only fire declared events.');
            }

            EventAction.performPropagationBehavior(context.container, null, name, eventPayload, eventModel, {
              preservePayloadRowContextMetadata: eventPayload !== payload,
            });
            return resolved;
          });
        });

      /**
       * Fires a notification event with the given options.
       *
       * @param {Object} options - Options for the notification event.
       * @param {String} [options.summary] - The summary of the event.
       * @param {String} [options.message] - The message of the event.
       * @param {'persist'|'transient'} [options.displayMode] - The display mode of the event.
       * @param {'error'|'warning'|'confirmation'|'info'} [options.type] - The type of the event.
       * @param {String} [options.key] - An optional key for the event.
       * if not provided, a 9 character alphanumeric UID is generated randomly.
       * @returns {Promise<undefined>}  A promise that resolves when the notification event is fired,
       * or rejects if an error occurs.
       */
      this.fireNotificationEvent = (options) => Promise.resolve()
        .then(() => {
          if (!context || !context.container) {
            throw new Error('Unable to fire event; no active container context');
          }

          const payload = FireNotificationEventAction.createPayload(options);

          const promiseOrNull =
            EventAction.fireEvent(context.container, Constants.NOTIFICATION_EVENT, payload, { target: this._target });

          if (!promiseOrNull) {
            throw new Error(`Unable to find target: ${this._target}`);
          }

          return promiseOrNull;
        });

      /**
       * Sets the target container for the start of event propagation (events bubble 'up' the containment).
       *
       * @param {String} target - The target container,
       * specifies which container the event should 'start' bubbling from.
       * @returns {EventHelper} The EventHelper instance for chaining.
       */
      this.target = (target) => {
        this._target = target;
        return this;
      }

      /**
       * Checks whether the current container has any dirty data variables.
       * It will check its current scope, then child containers and any possible extensions.
       * @return {String} 'dirty' if there is at least one dirty data variable found;
       * 'notDirty' otherwise
       */
      this.getDirtyDataStatus = () => {
        if (!context || !context.container) {
          throw new Error('Unable to get dirty data status; no active container context');
        }
        return context.container.getDirtyDataStatus();
      };
    }

    /**
     * Disposes of the EventHelper instance, releasing any held resources.
     *
     * This method resets the target container and removes references to event firing methods.
     * After calling dispose, the instance should not be used further.
     *
     * @example
     * const eventHelper = new EventHelper(context);
     * eventHelper.dispose(); // Dispose of the EventHelper instance
     */
    dispose() {
      this._target = null;
      delete this.target;
      delete this.fireCustomEvent;
      delete this.fireNotificationEvent;
      delete this.getDirtyDataStatus;
    }
  }

  return EventHelper;
});

